import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, Alert, IconButton, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Grid,
  Switch, FormControlLabel, Divider
} from '@mui/material';
import { Add, Edit, Delete, Home, Assessment, Refresh, ContentCopy, Scoreboard, BarChart, People, WarningAmber, EventNote } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { useStages } from '../hooks/useStages';
import { canView } from '../utils/permissions';

const API = process.env.REACT_APP_API_URL;

const MODES  = ['Written', 'Practical', 'Oral', 'Project', 'Portfolio'];
const CTYPES = ['CAT', 'Mid-Term', 'End-Term', 'Project', 'Practical', 'Assignment', 'Oral'];
const CATEGORIES = ['Formative', 'Summative'];
const TERMS  = [1, 2, 3];

// Summative = end-of-term exams; everything else defaults to Formative (CBC
// continuous assessment). Mirrors the server-side default.
const categoryOf = (type) => ['Mid-Term', 'End-Term'].includes(type) ? 'Summative' : 'Formative';
const mkComp = () => ({ _key: Date.now() + Math.random(), name: '', examId: '', examName: '', group: '', type: 'CAT', category: 'Formative', strand: '', maxScore: '' });
const emptyExam = { id: null, name: '', sequence: '', weight: '' };

const emptyForm = {
  academicYear: '', term: '', stageCode: '', learningAreaCode: '', learningAreaName: '',
  assessmentMode: 'Written', isPublished: false,
  components: [mkComp()], groups: []
};

const AssessmentSetupList = () => {
  const navigate = useNavigate();
  const { stages } = useStages();
  const role = (JSON.parse(localStorage.getItem('user') || '{}')).role;
  const isClassTeacher = role === 'classTeacher';
  // Subject teachers (role `user` without the academics module) are scoped to
  // their assigned subjects, exactly like class teachers. A role `user` with the
  // academics module (data-entry clerk) keeps the full, unscoped view.
  const isSubjectTeacher = role === 'user' && !canView('academics');
  const isTeacher = isClassTeacher || isSubjectTeacher;
  const [teachScope, setTeachScope] = useState(null); // { classTeacherOf, subjectClasses }
  const [setups,    setSetups]    = useState([]);
  const [years,     setYears]     = useState([]);
  const [areas,     setAreas]     = useState([]);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [dialogOpen,setDialogOpen]= useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dialogOpen, value: form, onClose: () => setDialogOpen(false) });
  const [saving,    setSaving]    = useState(false);
  const [cloneOf,   setCloneOf]   = useState(null); // source setup when cloning
  const [stageSubjects, setStageSubjects] = useState(null); // Set of areaCodes for the form's stage/year (null = not loaded/no restriction)
  const [filterY,   setFilterY]   = useState('');
  const [filterT,   setFilterT]   = useState('');
  const [filterS,   setFilterS]   = useState('');
  const [confirmDlg,setConfirmDlg]= useState(null);
  const [copyOpen,  setCopyOpen]  = useState(false);
  const [copyForm,  setCopyForm]  = useState({ academicYear: '', term: '', stageCode: '' });
  const [copying,   setCopying]   = useState(false);
  // Term → Exam → Subject → Papers: exams of the filtered term/stage, of the open
  // setup dialog's term/stage, and of the Manage Exams dialog's context.
  const [filterE,     setFilterE]     = useState('');
  const [filterExams, setFilterExams] = useState([]);
  const [dialogExams, setDialogExams] = useState([]);
  const [examCtx,     setExamCtx]     = useState(null); // { academicYear, term, stageCode } | null
  const [ctxExams,    setCtxExams]    = useState([]);
  const [examForm,    setExamForm]    = useState(emptyExam);
  const [examAllStages, setExamAllStages] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const hasFilter = Boolean(filterY || filterT || filterS);

  // Reference data for the dropdowns — loaded once, independent of filters.
  const loadMeta = useCallback(async () => {
    try {
      const [yRes, aRes] = await Promise.all([
        axios.get(`${API}/api/academics/years`, { headers }),
        axios.get(`${API}/api/academics/learning-areas`, { headers }),
      ]);
      setYears(yRes.data);
      setAreas(aRes.data);
    } catch { setError('Failed to load'); }
  }, []); // eslint-disable-line

  useEffect(() => { loadMeta(); }, [loadMeta]);

  // Restrict the Learning Area dropdown to the subjects actually assigned to the
  // chosen stage/year (StageLearningArea) — so a G10 setup can only target G10's
  // subjects, not the whole catalogue. Examinable subjects only (non-examinable
  // ones like Group Work aren't scored, so they get no assessment setup).
  useEffect(() => {
    if (!dialogOpen || !form.academicYear || !form.stageCode) { setStageSubjects(null); return; }
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/academics/stage-learning-areas?year=${form.academicYear}&stageCode=${form.stageCode}`, { headers });
        const rows = data || [];
        // No subjects configured for this stage/year → don't restrict (null).
        setStageSubjects(rows.length ? new Set(rows.filter(a => a.isExaminable !== false).map(a => a.areaCode)) : null);
      } catch { setStageSubjects(null); }
    })();
  }, [dialogOpen, form.academicYear, form.stageCode]); // eslint-disable-line

  // Setups load ONLY when a filter is applied — keeps the page blank by default
  // so it doesn't dump every stage/subject/term at once.
  const load = useCallback(async () => {
    if (!(filterY || filterT || filterS)) { setSetups([]); return; }
    try {
      const params = new URLSearchParams();
      if (filterY) params.set('academicYear', filterY);
      if (filterT) params.set('term', filterT);
      if (filterS) params.set('stageCode', filterS);
      const { data } = await axios.get(`${API}/api/academics/assessment-setups?${params}`, { headers });
      setSetups(data);
    } catch { setError('Failed to load'); }
  }, [filterY, filterT, filterS]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const fetchExams = useCallback(async (y, t, s) => {
    if (!y || !t || !s) return [];
    try {
      const { data } = await axios.get(
        `${API}/api/academics/assessment-exams?academicYear=${encodeURIComponent(y)}&term=${t}&stageCode=${encodeURIComponent(s)}`, { headers });
      return data || [];
    } catch { return []; }
  }, []); // eslint-disable-line

  // Exams of the filtered term/stage (needs all three filters) → Exam filter options.
  useEffect(() => {
    fetchExams(filterY, filterT, filterS).then(list => {
      setFilterExams(list);
      setFilterE(prev => (list.some(e => e._id === prev) ? prev : ''));
    });
  }, [filterY, filterT, filterS, fetchExams]);

  // Exams of the term/stage chosen in the setup dialog. Re-links each paper to the
  // matching exam of that term/stage (by id, else by name) so cloning to another
  // stage/term keeps its structure.
  useEffect(() => {
    if (!dialogOpen) return;
    fetchExams(form.academicYear, form.term, form.stageCode).then(list => {
      setDialogExams(list);
      setForm(f => {
        let changed = false;
        const components = f.components.map(c => {
          if (!c.examId && !c.examName) return c;
          const m = list.find(e => e._id === c.examId) || list.find(e => e.name === c.examName);
          if (m && m._id === c.examId && m.name === c.examName) return c;
          changed = true;
          return m ? { ...c, examId: m._id, examName: m.name } : { ...c, examId: '' };
        });
        if (!changed) return f;
        const groups = (f.groups || []).map(g => {
          const src = f.components.find(c => c.examId === g.examId);
          const m = list.find(e => e._id === g.examId) || list.find(e => e.name === src?.examName);
          return m ? { ...g, examId: m._id } : g;
        });
        return { ...f, components, groups };
      });
    });
  }, [dialogOpen, form.academicYear, form.term, form.stageCode, fetchExams]); // eslint-disable-line

  // Teachers: load teaching scope (class-teacher classes + allocated subjects) and
  // prefill the Stage + Year filters, so the page is scoped to what they can manage.
  useEffect(() => {
    if (!isTeacher) return;
    (async () => {
      try {
        const [sRes, yRes] = await Promise.all([
          axios.get(`${API}/api/academics/class-teacher/teaching-scope`, { headers }),
          axios.get(`${API}/api/academics/years`, { headers }),
        ]);
        setTeachScope(sRes.data);
        const first = (sRes.data.classTeacherOf || [])[0] || (sRes.data.subjectClasses || [])[0];
        if (first) setFilterS(first.stageCode);
        const active = (yRes.data || []).find(y => y.isActive);
        if (active) setFilterY(active.year);
      } catch { /* non-fatal */ }
    })();
  }, []); // eslint-disable-line

  // Stages a teacher can manage; subjects allowed for a stage (all for a stage they
  // class-teach, else only their allocated subjects).
  const stageOptions = useMemo(() => {
    if (!isTeacher || !teachScope) return stages;
    const codes = new Set([
      ...((teachScope.classTeacherOf) || []).map(c => c.stageCode),
      ...((teachScope.subjectClasses) || []).map(c => c.stageCode),
    ]);
    return stages.filter(s => codes.has(s.code));
  }, [isTeacher, teachScope, stages]);

  const allowedAreasForStage = useCallback((stageCode) => {
    if (!isTeacher || !teachScope || !stageCode) return null; // null = no restriction
    const anyCT = (teachScope.classTeacherOf || []).some(c => c.stageCode === stageCode);
    if (anyCT) return null; // class teacher of this stage → all subjects
    return new Set((teachScope.subjectClasses || [])
      .filter(sc => sc.stageCode === stageCode).map(sc => sc.areaCode));
  }, [isTeacher, teachScope]);

  const openCreate = () => {
    setEditing(null); setCloneOf(null);
    setForm({ ...emptyForm, components: [mkComp()], groups: [], academicYear: filterY || '', term: filterT || '', stageCode: filterS || '' });
    setError(''); setDialogOpen(true);
  };
  const openEdit = (s) => {
    setCloneOf(null); setEditing(s);
    setForm({
      academicYear:     s.academicYear,
      term:             s.term,
      stageCode:        s.stageCode,
      learningAreaCode: s.learningAreaCode,
      learningAreaName: s.learningAreaName || '',
      assessmentMode:   s.assessmentMode || 'Written',
      isPublished:      s.isPublished || false,
      components:       s.components.map(c => ({ ...c, examId: c.examId || '', _key: Math.random() })),
      groups:           (s.groups || []).map(g => ({ ...g }))
    });
    setError(''); setDialogOpen(true);
  };

  const openClone = (s) => {
    setCloneOf(s); setEditing(null); // treat as new — key fields are editable
    setForm({
      academicYear:     s.academicYear,
      term:             s.term,
      stageCode:        s.stageCode,
      learningAreaCode: s.learningAreaCode,
      learningAreaName: s.learningAreaName || '',
      assessmentMode:   s.assessmentMode || 'Written',
      isPublished:      s.isPublished || false,
      components:       s.components.map(c => ({ ...c, examId: c.examId || '', _key: Math.random() })),
      groups:           (s.groups || []).map(g => ({ ...g }))
    });
    setError(''); setDialogOpen(true);
  };

  // ── Copy Set: duplicate ALL currently-listed setups (with their components)
  // to a new Year/Term/Stage. e.g. copy G2's setups to G1 which share the setup.
  const openCopySet = () => {
    setCopyForm({ academicYear: filterY || '', term: filterT || '', stageCode: '' });
    setError(''); setCopyOpen(true);
  };

  const handleCopySet = async () => {
    if (!copyForm.academicYear) { setError('Target Year is required'); return; }
    if (!copyForm.term)         { setError('Target Term is required'); return; }
    if (!copyForm.stageCode)    { setError('Target Stage is required'); return; }
    if (!setups.length)         { setError('Nothing to copy'); return; }
    setCopying(true); setError('');
    let created = 0, skipped = 0;
    const errs = [];
    // Exams are per year/term/stage, so recreate the ones the source setups use on
    // the target (matched by name; weight/order carried over) and remap paper links.
    const srcTermStage = [...new Set(setups.map(s => `${s.academicYear}|${s.term}|${s.stageCode}`))];
    const srcExamById = {};
    for (const k of srcTermStage) {
      const [y, t, st] = k.split('|');
      (await fetchExams(y, t, st)).forEach(e => { srcExamById[e._id] = e; });
    }
    const tgtByName = {};
    (await fetchExams(copyForm.academicYear, copyForm.term, copyForm.stageCode)).forEach(e => { tgtByName[e.name] = e; });
    const neededNames = [...new Set(setups.flatMap(s => s.components.map(c => c.examName).filter(Boolean)))];
    for (const nm of neededNames) {
      if (tgtByName[nm]) continue;
      const src = Object.values(srcExamById).find(e => e.name === nm);
      try {
        const { data } = await axios.post(`${API}/api/academics/assessment-exams`, {
          academicYear: copyForm.academicYear, term: parseInt(copyForm.term, 10), stageCode: copyForm.stageCode,
          name: nm, sequence: src?.sequence || 1, weight: src?.weight || 0,
        }, { headers });
        tgtByName[nm] = data.created[0];
      } catch (err) { errs.push(`Exam ${nm}: ${err.response?.data?.error || 'failed'}`); }
    }
    const nameOfExam = (id) => srcExamById[id]?.name;
    for (const s of setups) {
      const payload = {
        academicYear:     copyForm.academicYear,
        term:             parseInt(copyForm.term, 10),
        stageCode:        copyForm.stageCode,
        learningAreaCode: s.learningAreaCode,
        learningAreaName: s.learningAreaName || areaName(s),
        assessmentMode:   s.assessmentMode || 'Written',
        isPublished:      false, // copies start as Draft so they can be adjusted
        components:       s.components.map(c => ({ name: c.name, examId: tgtByName[c.examName]?._id || null, group: c.group || '', type: c.type || 'CAT', category: c.category || categoryOf(c.type), strand: c.strand || '', subStrand: c.subStrand || '', maxScore: c.maxScore })),
        groups:           (s.groups || []).map(g => ({ examId: tgtByName[nameOfExam(g.examId)]?._id || null, name: g.name, weight: g.weight })).filter(g => g.examId),
      };
      try {
        await axios.post(`${API}/api/academics/assessment-setups`, payload, { headers });
        created++;
      } catch (err) {
        if (err.response?.status === 409) skipped++;
        else errs.push(`${s.learningAreaCode}: ${err.response?.data?.error || 'failed'}`);
      }
    }
    setCopying(false); setCopyOpen(false);
    const parts = [`${created} setup(s) copied to ${copyForm.stageCode} · Term ${copyForm.term} · ${copyForm.academicYear}`];
    if (skipped)      parts.push(`${skipped} already existed`);
    if (errs.length)  parts.push(`${errs.length} failed`);
    setSuccess(parts.join(' · '));
    setTimeout(() => setSuccess(''), 6000);
    // Jump the filters to the target so the copied setups are shown.
    setFilterY(copyForm.academicYear); setFilterT(parseInt(copyForm.term, 10)); setFilterS(copyForm.stageCode);
  };

  // Resolve a learning area's display name from the loaded areas list, falling
  // back to any stored name, then the code.
  const areaName = (s) => areas.find(a => a.areaCode === s.learningAreaCode)?.areaName || s.learningAreaName || s.learningAreaCode;

  const handleF = (e) => {
    const { name, value } = e.target;
    if (name === 'learningAreaCode') {
      const la = areas.find(a => a.areaCode === value);
      setForm(f => ({ ...f, learningAreaCode: value, learningAreaName: la?.areaName || '' }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  // With an Exam filter applied, the dialog shows only that exam's papers (e.g. End-Term
  // without the Opener) and new papers join that exam.
  const examScope = (editing || cloneOf) && filterE ? filterE : '';
  const inScope = (c) => !examScope || String(c.examId) === String(examScope);
  const addComp    = () => setForm(f => ({
    ...f,
    components: [...f.components, { ...mkComp(), ...(examScope ? { examId: examScope, examName: dialogExams.find(e => e._id === examScope)?.name || '' } : {}) }],
  }));
  const removeComp = (key)    => setForm(f => ({ ...f, components: f.components.filter(c => c._key !== key) }));
  const updateComp = (key, field, val) =>
    setForm(f => ({ ...f, components: f.components.map(c => c._key === key ? { ...c, [field]: val } : c) }));

  const totalMax = form.components.reduce((s, c) => s + (parseFloat(c.maxScore) || 0), 0);
  // Max of the exam a paper belongs to (for its "share of exam" figure).
  const examMax = (examId) => form.components
    .filter(c => (c.examId || '') === (examId || '')).reduce((s, c) => s + (parseFloat(c.maxScore) || 0), 0);

  // Paper groups in use, per exam: (PP1+PP2)/160 × 60% + PP3 × 40%, e.g. Theory 60 / Practical 40.
  const usedGroups = useMemo(() => {
    const seen = new Map();
    form.components.forEach(c => {
      const g = (c.group || '').trim();
      if (c.examId && g) seen.set(`${c.examId}|${g}`, { examId: c.examId, name: g });
    });
    return [...seen.values()];
  }, [form.components]);
  // A paper's real share of its exam, honouring paper groups (science: PP1+PP2 pooled
  // 60% → 30% each when both are /80, PP3 40%). Mirrors examResult() in assessmentCalc.
  const paperShare = (c) => {
    const max = parseFloat(c.maxScore) || 0;
    const inExam = form.components.filter(x => (x.examId || '') === (c.examId || ''));
    const active = (form.groups || [])
      .filter(g => g.examId === c.examId && parseFloat(g.weight) > 0)
      .map(g => ({ name: g.name, weight: parseFloat(g.weight) }));
    const sum = (arr) => arr.reduce((s, x) => s + (parseFloat(x.maxScore) || 0), 0);
    if (!active.length) { const t = sum(inExam); return t > 0 ? max / t * 100 : null; }
    const g = active.find(a => a.name === (c.group || '').trim());
    if (g) { const t = sum(inExam.filter(x => (x.group || '').trim() === g.name)); return t > 0 ? g.weight * max / t : null; }
    const named = new Set(active.map(a => a.name));
    const leftover = Math.max(0, 100 - active.reduce((s, a) => s + a.weight, 0));
    const rest = inExam.filter(x => !named.has((x.group || '').trim()));
    const t = sum(rest);
    return leftover > 0 && t > 0 ? leftover * max / t : 0;
  };
  const groupWeight = (examId, name) => (form.groups || []).find(g => g.examId === examId && g.name === name)?.weight ?? '';
  const setGroupWeight = (examId, name, weight) => setForm(f => {
    const rest = (f.groups || []).filter(g => !(g.examId === examId && g.name === name));
    return { ...f, groups: [...rest, { examId, name, weight }] };
  });
  const examNameOf = (id) => dialogExams.find(e => e._id === id)?.name || '';

  // One-click weighting for numbered papers (PP1, PP2, PP3 / Paper 1…), per exam.
  //   science: PP1+PP2 pooled → Theory 60%, PP3 practical → Practical 40%
  //   clear:   plain total ÷ max
  const PAPER_RE = /^(pp|paper)\s*\.?\s*(\d+)$/i;
  const paperNo = (n) => { const m = PAPER_RE.exec((n || '').trim()); return m ? parseInt(m[2], 10) : null; };
  const paperExamIds = [...new Set(form.components.filter(c => c.examId && inScope(c) && paperNo(c.name) !== null).map(c => c.examId))];
  const applyPreset = (kind) => setForm(f => {
    let comps = f.components.map(c => ({ ...c }));
    let groups = [...(f.groups || [])];
    paperExamIds.forEach(examId => {
      const papers = comps.filter(c => c.examId === examId && paperNo(c.name) !== null)
        .sort((a, b) => paperNo(a.name) - paperNo(b.name));
      if (papers.length < 2 && kind !== 'clear') return;
      groups = groups.filter(g => g.examId !== examId);
      papers.forEach(p => { p.group = ''; });
      if (kind === 'science') {
        papers.forEach(p => { p.group = paperNo(p.name) >= 3 ? 'Practical' : 'Theory'; });
        groups.push({ examId, name: 'Theory', weight: 60 }, { examId, name: 'Practical', weight: 40 });
      }
    });
    const byKey = Object.fromEntries(comps.map(c => [c._key, c]));
    return { ...f, components: f.components.map(c => byKey[c._key] || c), groups };
  });

  const handleSave = async () => {
    if (!form.academicYear) { setError('Academic Year is required'); return; }
    if (!form.term)         { setError('Term is required'); return; }
    if (!form.stageCode)    { setError('Stage is required'); return; }
    if (!form.learningAreaCode) { setError('Learning Area is required'); return; }
    if (form.components.some(c => !c.name.trim())) { setError('All components need a name'); return; }
    if (form.components.some(c => !c.maxScore || parseFloat(c.maxScore) <= 0)) { setError('All components need a max score > 0'); return; }
    if (dialogExams.length && form.components.some(c => !c.examId)) { setError('Assign every paper to an exam (this term has exams defined)'); return; }
    const gSum = {};
    usedGroups.forEach(g => { gSum[g.examId] = (gSum[g.examId] || 0) + (parseFloat(groupWeight(g.examId, g.name)) || 0); });
    const badExam = Object.keys(gSum).find(k => gSum[k] > 100.0001);
    if (badExam) { setError(`Paper group weights for ${examNameOf(badExam)} exceed 100%`); return; }
    setSaving(true); setError('');
    const payload = {
      ...form,
      term: parseInt(form.term, 10),
      components: form.components.map(({ _key, ...c }) => ({ ...c, examId: c.examId || null, maxScore: parseFloat(c.maxScore) })),
      groups: usedGroups
        .map(g => ({ examId: g.examId, name: g.name, weight: parseFloat(groupWeight(g.examId, g.name)) || 0 }))
        .filter(g => g.weight > 0),
    };
    try {
      if (editing) {
        await axios.put(`${API}/api/academics/assessment-setups/${editing._id}`, { ...payload, forceUpdate: true }, { headers });
        setSuccess('Updated');
      } else {
        await axios.post(`${API}/api/academics/assessment-setups`, payload, { headers });
        setSuccess('Created');
      }
      setDialogOpen(false); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  // ── Manage Exams (Opener / Mid-Term / End-Term…) for one year · term · stage ──
  const openExams = async (ctx) => {
    setExamCtx(ctx); setExamForm(emptyExam); setExamAllStages(false); setError('');
    setCtxExams(await fetchExams(ctx.academicYear, ctx.term, ctx.stageCode));
  };
  const refreshExamLists = async () => {
    if (!examCtx) return;
    setCtxExams(await fetchExams(examCtx.academicYear, examCtx.term, examCtx.stageCode));
    setFilterExams(await fetchExams(filterY, filterT, filterS));
    if (dialogOpen) setDialogExams(await fetchExams(form.academicYear, form.term, form.stageCode));
    load();
  };
  const saveExam = async () => {
    if (!examForm.name.trim()) { setError('Exam name is required'); return; }
    setError('');
    try {
      const body = { name: examForm.name.trim(), sequence: examForm.sequence || undefined, weight: examForm.weight === '' ? 0 : examForm.weight };
      if (examForm.id) {
        await axios.put(`${API}/api/academics/assessment-exams/${examForm.id}`, body, { headers });
      } else {
        await axios.post(`${API}/api/academics/assessment-exams`, {
          ...examCtx, ...body,
          ...(examAllStages ? { stageCodes: stages.map(s => s.code) } : {}),
        }, { headers });
      }
      setExamForm(emptyExam); setExamAllStages(false);
      await refreshExamLists();
    } catch (err) { setError(err.response?.data?.error || 'Could not save exam'); }
  };
  const deleteExam = async (e) => {
    setError('');
    try {
      await axios.delete(`${API}/api/academics/assessment-exams/${e._id}`, { headers });
      await refreshExamLists();
    } catch (err) { setError(err.response?.data?.error || 'Could not delete exam'); }
  };
  const examWeightTotal = ctxExams.reduce((s, e) => s + (e.weight || 0), 0);

  // Exam filter narrows the table to setups that have papers in that exam and
  // shows only those papers.
  const visibleSetups = useMemo(() => (filterE
    ? setups.filter(s => s.components.some(c => String(c.examId) === filterE))
    : setups), [setups, filterE]);
  const compsOf = (s) => (filterE ? s.components.filter(c => String(c.examId) === filterE) : s.components);

  const handleDelete = (s) => setConfirmDlg(s);

  const confirmDelete = async () => {
    const s = confirmDlg;
    setConfirmDlg(null);
    try {
      await axios.delete(`${API}/api/academics/assessment-setups/${s._id}`, { headers });
      setSuccess('Deleted'); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3} sx={{ position: { xs: 'sticky', sm: 'relative' }, top: { xs: 0, sm: 'auto' }, zIndex: 10, mb: { xs: 0, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Assessment sx={{ color: '#00838f', fontSize: { xs: 20, sm: 24 } }} />
              <Typography variant="h5" sx={{ color: '#00838f', fontWeight: 700, fontSize: { xs: '1.15rem', sm: '1.5rem' } }}>Assessment Setup</Typography>
              <Chip label={visibleSetups.length} size="small" sx={{ bgcolor: '#00838f22', color: '#00838f' }} />
            </Box>
            <Box display="flex" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
              <Tooltip title="Refresh"><IconButton onClick={load} size="small" sx={{ bgcolor: 'background.paper' }}><Refresh sx={{ fontSize: { xs: 18, sm: 24 } }} /></IconButton></Tooltip>
              <Tooltip title="Score Entry"><IconButton onClick={() => navigate('/academics/score-entry')} size="small" sx={{ bgcolor: '#e8eaf6' }}><Scoreboard sx={{ color: '#283593', fontSize: { xs: 18, sm: 24 } }} /></IconButton></Tooltip>
              <Tooltip title="Assessment Reports"><IconButton onClick={() => navigate('/academics/assessment-report')} size="small" sx={{ bgcolor: '#e0f2f1' }}><BarChart sx={{ color: '#004d40', fontSize: { xs: 18, sm: 24 } }} /></IconButton></Tooltip>
              <Tooltip title="Learners List"><IconButton onClick={() => navigate('/learners')} size="small" sx={{ bgcolor: '#fbe9e7' }}><People sx={{ color: '#e65100', fontSize: { xs: 18, sm: 24 } }} /></IconButton></Tooltip>
              <Tooltip title="Back to Academics"><IconButton onClick={() => navigate('/academics')} size="small" sx={{ bgcolor: 'background.paper' }}><Home sx={{ fontSize: { xs: 18, sm: 24 } }} /></IconButton></Tooltip>
              {!isTeacher && (
                <Tooltip title={filterY && filterT && filterS ? 'Manage exams for this term & stage' : 'Select Year, Term and Stage to manage exams'}>
                  <span>
                    <Button variant="outlined" startIcon={<EventNote />} size="small" disabled={!(filterY && filterT && filterS)}
                      onClick={() => openExams({ academicYear: filterY, term: parseInt(filterT, 10), stageCode: filterS })}
                      sx={{ color: '#6a1b9a', borderColor: '#6a1b9a', display: { xs: 'none', sm: 'inline-flex' } }}>Exams</Button>
                  </span>
                </Tooltip>
              )}
              {setups.length > 0 && (
                <Button variant="outlined" startIcon={<ContentCopy />} onClick={openCopySet} size="small"
                  sx={{ color: '#00838f', borderColor: '#00838f', display: { xs: 'none', sm: 'inline-flex' } }}>Copy Set</Button>
              )}
              <Button variant="contained" startIcon={<Add />} onClick={openCreate} size="small" sx={{ bgcolor: '#00838f', '&:hover': { bgcolor: '#006064' }, display: { xs: 'none', sm: 'inline-flex' } }}>Add Setup</Button>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          <Typography variant="body2" color="text.secondary" mb={2} sx={{ display: { xs: 'none', sm: 'block' } }}>
            A term can hold several exams (Opener, Mid-Term, End-Term…). Each subject's papers (e.g. PP1, PP2, PP3) belong to an exam;
            papers inside an exam can be grouped and weighted (e.g. PP1+PP2 60%, practical PP3 40%). Exam weights set the termly average.
          </Typography>

          {/* Filters */}
          <Box display="flex" gap={{ xs: 1, sm: 2 }} mb={2} flexWrap="wrap" sx={{ px: { xs: 1, sm: 0 } }}>
            <TextField select label="Year" size="small" value={filterY} onChange={e => setFilterY(e.target.value)} sx={{ minWidth: { xs: 'calc(50% - 8px)', sm: 140 } }}>
              <MenuItem value=""><em>All years</em></MenuItem>
              {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
            </TextField>
            <TextField select label="Term" size="small" value={filterT} onChange={e => setFilterT(e.target.value)} sx={{ minWidth: { xs: 'calc(50% - 8px)', sm: 120 } }}>
              <MenuItem value=""><em>All terms</em></MenuItem>
              {TERMS.map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
            </TextField>
            <TextField select label="Stage" size="small" value={filterS} onChange={e => setFilterS(e.target.value)} sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <MenuItem value=""><em>All stages</em></MenuItem>
              {stageOptions.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
            </TextField>
            <TextField select label="Exam" size="small" value={filterE} onChange={e => setFilterE(e.target.value)}
              disabled={!filterExams.length} sx={{ minWidth: { xs: '100%', sm: 170 } }}
              helperText={filterY && filterT && filterS && !filterExams.length ? 'No exams defined yet' : undefined}>
              <MenuItem value=""><em>All exams</em></MenuItem>
              {filterExams.map(e => <MenuItem key={e._id} value={e._id}>{e.name}{e.weight ? ` (${e.weight}%)` : ''}</MenuItem>)}
            </TextField>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {!hasFilter ? (
            <Box sx={{ py: 6, textAlign: 'center', px: 2 }}>
              <Assessment sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Select a <strong>Year</strong>, <strong>Term</strong> or <strong>Stage</strong> above to view assessment setups.
              </Typography>
              <Button size="small" startIcon={<Add />} onClick={openCreate} sx={{ mt: 1.5 }}>Add Setup</Button>
            </Box>
          ) : (
          <Box sx={{ overflowX: 'auto', mx: { xs: 1, sm: 0 } }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontSize: { xs: 10, sm: 12 } }}><strong>Stage</strong></TableCell>
                  <TableCell sx={{ fontSize: { xs: 10, sm: 12 } }}><strong>Learning Area</strong></TableCell>
                  <TableCell align="center" sx={{ fontSize: { xs: 10, sm: 12 } }}><strong>Term</strong></TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: 12 }}><strong>Mode</strong></TableCell>
                  <TableCell sx={{ fontSize: { xs: 9, sm: 12 } }}><strong>Exams · Papers</strong></TableCell>
                  <TableCell align="center" sx={{ fontSize: { xs: 10, sm: 12 } }}><strong>Max</strong></TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: 12 }}><strong>Status</strong></TableCell>
                  <TableCell align="center" sx={{ fontSize: { xs: 9, sm: 12 } }}><strong>Act</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleSetups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" py={2}>No setups yet. <Button size="small" onClick={openCreate}>Add Setup</Button></Typography>
                    </TableCell>
                  </TableRow>
                ) : visibleSetups.map(s => (
                  <TableRow key={s._id} hover>
                    <TableCell sx={{ fontSize: { xs: 10, sm: 12 } }}><strong>{s.stageCode}</strong></TableCell>
                    <TableCell sx={{ fontSize: { xs: 10, sm: 11 } }}>{areaName(s)}</TableCell>
                    <TableCell align="center" sx={{ fontSize: { xs: 10, sm: 11 } }}><Chip label={`T${s.term}`} size="small" variant="outlined" /></TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: 11 }}><Typography variant="caption">{s.assessmentMode}</Typography></TableCell>
                    <TableCell sx={{ fontSize: { xs: 9, sm: 11 } }}>
                      <Box display="flex" flexDirection="column" gap={0.25}>
                        {(() => {
                          const comps = compsOf(s);
                          const byExam = [];
                          comps.forEach(c => {
                            const k = c.examName || '—';
                            let row = byExam.find(r => r.k === k);
                            if (!row) { row = { k, items: [] }; byExam.push(row); }
                            row.items.push(c);
                          });
                          return byExam.map(r => (
                            <Box key={r.k} display="flex" alignItems="center" gap={0.25} flexWrap="wrap">
                              {r.k !== '—' && <Typography variant="caption" sx={{ fontWeight: 700, color: '#6a1b9a', mr: 0.5 }}>{r.k}:</Typography>}
                              {r.items.map((c, i) => (
                                <Tooltip key={i} title={`${c.name}${c.group ? ' · ' + c.group : ''} — /${c.maxScore}`}>
                                  <Chip label={c.name.length > 6 ? c.name.substring(0, 6) : c.name} size="small" variant="outlined" sx={{ fontSize: 9 }} />
                                </Tooltip>
                              ))}
                            </Box>
                          ));
                        })()}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: { xs: 10, sm: 11 }, fontWeight: 700 }}>
                      /{compsOf(s).reduce((sum, c) => sum + c.maxScore, 0)}
                    </TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: 11 }}>
                      <Chip label={s.isPublished ? 'Pub' : 'Draft'} size="small" color={s.isPublished ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: { xs: 8, sm: 11 } }}>
                      <Tooltip title="Clone"><IconButton size="small" sx={{ color: '#00838f', p: 0.25 }} onClick={() => openClone(s)}><ContentCopy sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                      <Tooltip title="Edit"><IconButton size="small" color="primary" sx={{ p: 0.25 }} onClick={() => openEdit(s)}><Edit sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" sx={{ p: 0.25 }} onClick={() => handleDelete(s)}><Delete sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          )}

          {/* Mobile action buttons */}
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'center', gap: 1, mt: 2 }}>
            {hasFilter && setups.length > 0 && (
              <Button variant="outlined" startIcon={<ContentCopy />} onClick={openCopySet} sx={{ color: '#00838f', borderColor: '#00838f' }}>Copy Set</Button>
            )}
            <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={{ bgcolor: '#00838f', '&:hover': { bgcolor: '#006064' } }}>Add Setup</Button>
          </Box>
        </CardContent>
      </Card>

      {/* ── Manage Exams dialog ── */}
      <Dialog open={Boolean(examCtx)} onClose={() => setExamCtx(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#6a1b9a' }}>
          <EventNote /> Exams — {examCtx?.stageCode} · Term {examCtx?.term} · {examCtx?.academicYear}
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Add the exams sat this term (e.g. Opener, Mid-Term, End-Term). The <strong>weight</strong> is each exam's share of the
            termly subject average; leave all at 0 to weight them equally.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>#</strong></TableCell>
                <TableCell><strong>Exam</strong></TableCell>
                <TableCell align="center"><strong>Weight %</strong></TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {ctxExams.length === 0 && (
                <TableRow><TableCell colSpan={4} align="center"><Typography variant="caption" color="text.secondary">No exams yet</Typography></TableCell></TableRow>
              )}
              {ctxExams.map(e => (
                <TableRow key={e._id} hover>
                  <TableCell>{e.sequence}</TableCell>
                  <TableCell>{e.name}</TableCell>
                  <TableCell align="center">{e.weight || '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => setExamForm({ id: e._id, name: e.name, sequence: e.sequence, weight: e.weight || '' })}><Edit sx={{ fontSize: 16 }} /></IconButton>
                    <IconButton size="small" color="error" onClick={() => deleteExam(e)}><Delete sx={{ fontSize: 16 }} /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {ctxExams.some(e => e.weight > 0) && (
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell colSpan={2}><Typography variant="caption" fontWeight={700}>TOTAL WEIGHT</Typography></TableCell>
                  <TableCell align="center">
                    <Typography variant="caption" fontWeight={700} color={Math.abs(examWeightTotal - 100) < 0.01 ? 'success.main' : 'warning.main'}>
                      {examWeightTotal}%{Math.abs(examWeightTotal - 100) < 0.01 ? '' : ' (should be 100)'}
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={700} mb={1}>{examForm.id ? 'Edit exam' : 'Add exam'}</Typography>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            <TextField size="small" label="Exam name" value={examForm.name} sx={{ flex: 1, minWidth: 160 }}
              onChange={e => setExamForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Opener" />
            <TextField size="small" label="Order" type="number" value={examForm.sequence} sx={{ width: 80 }}
              onChange={e => setExamForm(f => ({ ...f, sequence: e.target.value }))} />
            <TextField size="small" label="Weight %" type="number" value={examForm.weight} sx={{ width: 100 }}
              inputProps={{ min: 0, max: 100 }} onChange={e => setExamForm(f => ({ ...f, weight: e.target.value }))} />
          </Box>
          {!examForm.id && (
            <FormControlLabel sx={{ mt: 1 }}
              control={<Switch size="small" checked={examAllStages} onChange={e => setExamAllStages(e.target.checked)} />}
              label="Create for all stages (this term)" />
          )}
        </DialogContent>
        <DialogActions>
          {examForm.id && <Button onClick={() => setExamForm(emptyExam)}>Cancel edit</Button>}
          <Button variant="contained" onClick={saveExam} sx={{ bgcolor: '#6a1b9a', '&:hover': { bgcolor: '#4a148c' } }}>
            {examForm.id ? 'Update exam' : 'Add exam'}
          </Button>
          <Button onClick={() => setExamCtx(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Copy Set dialog ── */}
      <Dialog open={copyOpen} onClose={() => !copying && setCopyOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#00838f' }}>
          <ContentCopy /> Copy Set
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Copies all <strong>{setups.length}</strong> setup(s) currently shown (with their components) to a new target.
            Useful when grades share the same assessment structure (e.g. copy G2 → G1). Copies are created as <strong>Draft</strong>.
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField select label="Target Year *" value={copyForm.academicYear} fullWidth size="small"
                onChange={e => setCopyForm(f => ({ ...f, academicYear: e.target.value }))}>
                <MenuItem value=""><em>— Select —</em></MenuItem>
                {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Target Term *" value={copyForm.term} fullWidth size="small"
                onChange={e => setCopyForm(f => ({ ...f, term: e.target.value }))}>
                <MenuItem value=""><em>—</em></MenuItem>
                {TERMS.map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select label="Target Stage *" value={copyForm.stageCode} fullWidth size="small"
                onChange={e => setCopyForm(f => ({ ...f, stageCode: e.target.value }))}>
                <MenuItem value=""><em>— Select —</em></MenuItem>
                {stageOptions.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyOpen(false)} disabled={copying}>Cancel</Button>
          <Button variant="contained" onClick={handleCopySet} disabled={copying}
            sx={{ bgcolor: '#00838f', '&:hover': { bgcolor: '#006064' } }}>
            {copying ? 'Copying…' : `Copy ${setups.length} Setup(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirm Delete dialog ── */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>Delete assessment setup for <strong>{confirmDlg?.stageCode} · {confirmDlg?.learningAreaCode} Term {confirmDlg?.term}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog */}
      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: { xs: 14, sm: 16 } }}>
          {editing
            ? `Edit — ${editing.stageCode} · ${editing.learningAreaCode} Term ${editing.term}`
            : cloneOf
            ? `Clone — ${cloneOf.stageCode} · ${cloneOf.learningAreaCode} Term ${cloneOf.term} (change target below)`
            : 'Add Assessment Setup'}
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField select label="Academic Year *" name="academicYear" value={form.academicYear}
                onChange={handleF} fullWidth size="small" disabled={!!editing}>
                <MenuItem value=""><em>— Select —</em></MenuItem>
                {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField select label="Term *" name="term" value={form.term}
                onChange={handleF} fullWidth size="small" disabled={!!editing}>
                <MenuItem value=""><em>—</em></MenuItem>
                {TERMS.map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField select label="Stage *" name="stageCode" value={form.stageCode}
                onChange={handleF} fullWidth size="small" disabled={!!editing}>
                <MenuItem value=""><em>— Select —</em></MenuItem>
                {stageOptions.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Learning Area *" name="learningAreaCode" value={form.learningAreaCode}
                onChange={handleF} fullWidth size="small" disabled={!!editing}>
                <MenuItem value=""><em>— Select —</em></MenuItem>
                {(() => {
                  const allowed = allowedAreasForStage(form.stageCode);
                  return areas
                    .filter(a => !allowed || allowed.has(a.areaCode))          // teacher's own subjects
                    .filter(a => !stageSubjects || stageSubjects.has(a.areaCode)) // subjects assigned to this stage/year
                    .map(a => <MenuItem key={a.areaCode} value={a.areaCode}>{a.areaName}</MenuItem>);
                })()}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Assessment Mode" name="assessmentMode" value={form.assessmentMode}
                onChange={handleF} fullWidth size="small">
                {MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} display="flex" alignItems="center">
              <FormControlLabel
                control={<Switch checked={form.isPublished} onChange={e => setForm(f => ({ ...f, isPublished: e.target.checked }))} color="success" />}
                label={form.isPublished ? 'Published (score entry enabled)' : 'Draft'}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2" fontWeight={700}>Papers / Components</Typography>
                <Box display="flex" gap={1}>
                  {!isTeacher && form.academicYear && form.term && form.stageCode && (
                    <Button size="small" startIcon={<EventNote />} sx={{ color: '#6a1b9a' }}
                      onClick={() => openExams({ academicYear: form.academicYear, term: parseInt(form.term, 10), stageCode: form.stageCode })}>Manage exams</Button>
                  )}
                  <Button size="small" startIcon={<Add />} onClick={addComp}>Add Paper</Button>
                </Box>
              </Box>
              {paperExamIds.length > 0 && (
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
                  <Typography variant="caption" color="text.secondary">Paper weighting:</Typography>
                  <Button size="small" variant="outlined" onClick={() => applyPreset('science')}>Science (PP1+PP2 60% · PP3 40%)</Button>
                  <Button size="small" onClick={() => applyPreset('clear')}>Plain total (by paper max)</Button>
                </Box>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Exam</strong></TableCell>
                    <TableCell><strong>Paper</strong></TableCell>
                    <TableCell><strong>Group</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Category</strong></TableCell>
                    <TableCell><strong>Strand</strong></TableCell>
                    <TableCell align="center"><strong>Max Score</strong></TableCell>
                    <TableCell align="center"><strong>% of exam</strong></TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {form.components.filter(inScope).map(c => (
                    <TableRow key={c._key}>
                      <TableCell>
                        <TextField select size="small" value={c.examId || ''} sx={{ minWidth: 120 }}
                          onChange={e => {
                            const ex = dialogExams.find(x => x._id === e.target.value);
                            setForm(f => ({ ...f, components: f.components.map(x => x._key === c._key
                              ? { ...x, examId: e.target.value, examName: ex?.name || '' } : x) }));
                          }}
                          error={dialogExams.length > 0 && !c.examId}>
                          <MenuItem value=""><em>{dialogExams.length ? '— Pick exam —' : 'No exams yet'}</em></MenuItem>
                          {dialogExams.map(e => <MenuItem key={e._id} value={e._id}>{e.name}</MenuItem>)}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField size="small" value={c.name} onChange={e => updateComp(c._key, 'name', e.target.value)}
                          placeholder="e.g. PP1" sx={{ minWidth: 100 }} />
                      </TableCell>
                      <TableCell>
                        <TextField size="small" value={c.group || ''} onChange={e => updateComp(c._key, 'group', e.target.value)}
                          placeholder="optional" sx={{ minWidth: 90 }} />
                      </TableCell>
                      <TableCell>
                        <TextField select size="small" value={c.type}
                          onChange={e => setForm(f => ({ ...f, components: f.components.map(x =>
                            x._key === c._key ? { ...x, type: e.target.value, category: categoryOf(e.target.value) } : x) }))}
                          sx={{ minWidth: 120 }}>
                          {CTYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField select size="small" value={c.category || 'Formative'} onChange={e => updateComp(c._key, 'category', e.target.value)} sx={{ minWidth: 120 }}>
                          {CATEGORIES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField size="small" value={c.strand || ''} onChange={e => updateComp(c._key, 'strand', e.target.value)}
                          placeholder="Strand (optional)" sx={{ minWidth: 140 }} />
                      </TableCell>
                      <TableCell align="center">
                        <TextField size="small" type="number" value={c.maxScore} onChange={e => updateComp(c._key, 'maxScore', e.target.value)}
                          sx={{ width: 80 }} inputProps={{ min: 1 }} />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" color="text.secondary">
                          {paperShare(c) !== null ? `${paperShare(c).toFixed(1)}%` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => removeComp(c._key)} disabled={form.components.length <= 1}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={6}><Typography variant="caption" fontWeight={700}>{examScope ? `TOTAL (${examNameOf(examScope) || 'this exam'})` : 'TOTAL (all exams)'}</Typography></TableCell>
                    <TableCell align="center"><Typography variant="caption" fontWeight={700}>/{examScope ? examMax(examScope) : totalMax}</Typography></TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableBody>
              </Table>
              {usedGroups.length > 0 && (
                <Box sx={{ mt: 2, p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>Paper group weights</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Papers sharing a group are pooled (sum of marks ÷ sum of max), then groups are blended by weight —
                    e.g. Theory (PP1+PP2) 60% and Practical (PP3) 40%. Groups of an exam should total 100%; leave a weight blank/0 to ignore grouping.
                  </Typography>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    {usedGroups.filter(g => !examScope || g.examId === examScope).map(g => (
                      <TextField key={`${g.examId}|${g.name}`} size="small" type="number" sx={{ width: 190 }}
                        label={`${examNameOf(g.examId)} · ${g.name} %`}
                        value={groupWeight(g.examId, g.name)} inputProps={{ min: 0, max: 100 }}
                        onChange={e => setGroupWeight(g.examId, g.name, e.target.value)} />
                    ))}
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ bgcolor: '#00838f', '&:hover': { bgcolor: '#006064' } }}>
            {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssessmentSetupList;
